/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useMerchantWeightAllListData } from "../../../apis/merchantApis";
import { useFetchCountryWarehouseAllData } from "../../../apis/locationApis";
import { useServiceAllListData } from "../../../apis/orderApis";
import {
  useFetchAllPriceMatrixTireData,
  usePriceMatrixMutation,
  useUpdatePriceMatrixStatus,
  usePriceMatrixCreateMutation,
  usePriceMatrixUpdateMutation,
} from "../../../apis/priceMatrixApis";
import { useClientWeightListAllListData } from "../../../apis/shippingApis";
import NewSelectInput from "../../../components/NewSelectInput";
import NewinputField from "../../../components/NewInput";
import Pagination from "../../../components/common/Pagination";
import TableSkeleton from "../../../components/skeleton/TableSkeleton";
import ListView from "../../../components/common/ListView";
import NoDataFound from "../../../components/common/NoDataFound";
import ToggleButton from "../../../components/ToggleButton";
import { IconPencil } from "@tabler/icons-react";
import ViewCitiesPriceMatrixModal from "./ViewCitiesPriceMatrixModal";
import toast from "react-hot-toast";
import MessageModal from "../../../components/MessageModal";
import { useLanguage } from "../../../context/LanguageContext";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onToggle: () => void;
};

type WeightRow = {
  range: string;
  delivery_price: string;
  rangeId: string;
  from: string;
  to: string;
};

export default function PriceTab({ isOpen, onClose, onToggle }: Props) {
  const [pageNo, setPageNo] = useState<number>(1);
  const [limit] = useState<number>(10);
  const [forClient, setForClient] = useState(true);
  const [clientId, setClientId] = useState("");
  const [cityModalOpen, setCityModalOpen] = useState(false);
  const [selectedCities, setSelectedCities] = useState<string[]>([]);
  const [activeModal, setActiveModal] = useState<boolean>(false);
  const [selectedPriceMatrix, setSelectedPriceMatrix] = useState<any>(null);
  const [weightRows, setWeightRows] = useState<WeightRow[]>([]);
  const [isEdit, setIsEdit] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const { t } = useLanguage();

  const { data, refetch, isLoading, isFetching } = usePriceMatrixMutation(
    pageNo,
    limit
  );

  const { data: weightClients } = useMerchantWeightAllListData();
  const { data: weightData, refetch: fetchWeightRanges } =
    useClientWeightListAllListData(clientId || "");
  const { data: warehouses } = useFetchCountryWarehouseAllData();
  const { data: tireData } = useFetchAllPriceMatrixTireData();
  const { data: serviceData } = useServiceAllListData();
  const { mutate: createPriceMatrix, isPending: createPending } =
    usePriceMatrixCreateMutation();
  const { mutate: updatePriceMatrix, isPending: updatePending } =
    usePriceMatrixUpdateMutation();
  const { mutate: updateStatus, isPending: statusPending } =
    useUpdatePriceMatrixStatus();

  useEffect(() => {
    fetchWeightRanges();
  }, [clientId]);

  const {
    register,
    handleSubmit,
    clearErrors,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm();

  useEffect(() => {
    if (weightData && weightData.ranges && Array.isArray(weightData.ranges)) {
      const rows = weightData.ranges.map((range: any) => {
        const from = range.from;
        const to = range.to === "flat" ? "+" : `–${range.to}`;
        const displayRange = `${from}${to} kg`;
        return {
          range: displayRange,
          delivery_price: "",
          rangeId: range.id,
          from: range.from,
          to: range.to,
        };
      });
      setWeightRows(rows);
    } else if (clientId && (!weightData || !weightData.ranges)) {
      setWeightRows([]);
    }
  }, [weightData, clientId]);

  useEffect(() => {
    weightRows.forEach((_, index) => {
      register(`delivery_price_${index}`, {
        required: t("Rate is required"),
        pattern: {
          value: /^\d+(\.\d{1,2})?$/,
          message: t("Only numbers are allowed (up to 2 decimal places)"),
        },
        validate: {
          positive: (value) => {
            const num = parseFloat(value);
            return num > 0 || t("Rate must be greater than 0");
          },
          maxValue: (value) => {
            const num = parseFloat(value);
            return num <= 999999 || t("Rate cannot exceed 999,999");
          },
        },
      });
    });
  }, [weightRows, register]);

  const handleClientChange = (val: string) => {
    setValue("clientForId", val);
    setClientId(val);
    setWeightRows([]);
  };

  const handleEdit = (row: any) => {
    setIsEdit(true);
    setEditingId(row.id);

    const clientFor = row.clientId || row.clientForId || "";
    setForClient(!!clientFor);
    setClientId(clientFor);

    setValue("clientForId", clientFor);
    setValue("warehouseId", row.warehouseId || "");
    setValue("tireId", row.tireId || row.tireID || "");
    setValue("serviceType", row.serviceId || row.serviceID || "");
    setValue("return_price", row.returnFee || row.return_price || "0");
    setValue("cod_fee", row.codFee || row.cod_fee || "0");

    if (row.weightrangeprice && Array.isArray(row.weightrangeprice)) {
      const rows = row.weightrangeprice.map((wp: any) => {
        return {
          range: `${wp.from}${wp.to === "flat" ? "+" : `–${wp.to}`} kg`,
          rangeId: wp.rangeId || wp.id,
          from: wp.from,
          to: wp.to,
          delivery_price: wp.price || "",
        };
      });

      setWeightRows(rows);
    }

    onToggle();
  };

  // Cancel
  const handleCancel = () => {
    setIsEdit(false);
    setEditingId(null);
    setClientId("");
    setWeightRows([]);
    reset();
    onClose();
  };

  // Submit
  const onSubmit = (formData: any) => {
    if (forClient && !clientId) {
      toast.error(t("Please select a client"));
      return;
    }

    if (weightRows.length === 0) {
      toast.error(t("No weight ranges"));
      return;
    }

    const weightRangePrice = weightRows
      .map((row, index) => {
        const price = watch(`delivery_price_${index}`);
        if (!price || !/^\d+(\.\d{1,2})?$/.test(price.toString().trim())) {
          toast.error(
            `${t("Invalid price for")} ${row.range}. ${t("Only numbers are allowed.")}`
          );
          return null;
        }
        const numPrice = parseFloat(price.toString().trim());
        if (numPrice <= 0) {
          toast.error(`${t("Price for")} ${row.range} ${t("must be greater than 0")}`);
          return null;
        }
        return {
          rangeId: row.rangeId,
          from: row.from,
          to: row.to,
          price: price.toString().trim(),
        };
      })
      .filter(Boolean);

    if (weightRangePrice.length === 0) return;

    const payload: any = {
      tireId: formData.tireId,
      warehouseId: formData.warehouseId,
      serviceId: formData.serviceType,
      weightRangePrice,
      codFee: formData.cod_fee || "0",
      returnFee: formData.return_price || "0",
      shippingWeightId: weightData?.id,
    };

    if (forClient) {
      payload.clientForId = clientId;
      payload.shippingWeightId = weightData?.id;
    }

    if (isEdit && editingId) {
      updatePriceMatrix(
        { id: editingId, payload },
        {
          onSuccess: () => {
            toast.success(t("Updated!"));
            handleCancel();
            refetch();
          },
          onError: (err: any) => toast.error(err || t("Update failed")),
        }
      );
    } else {
      createPriceMatrix(payload, {
        onSuccess: () => {
          toast.success(t("Created!"));
          handleCancel();
          refetch();
        },
        onError: (err: any) => toast.error(err || t("Create failed")),
      });
    }
  };

  const handleViewCities = (cities: string[]) => {
    setSelectedCities(cities);
    setCityModalOpen(true);
  };

  const handleUpdateStatus = () => {
    if (!selectedPriceMatrix) return;
    updateStatus(
      {
        id: selectedPriceMatrix.id,
        status: selectedPriceMatrix.status === "ACTIVE" ? "INACTIVE" : "ACTIVE",
      },
      {
        onSuccess: () => {
          setActiveModal(false);
          refetch();
          toast.success(t("Status updated"));
        },
        onError: (error: any) => {
          toast.error(error.message || t("Failed"));
        },
      }
    );
  };
  const handlePaginationFetch = (page: number) => {
    setPageNo(page);
  };

  return (
    <div>
      <div className=" space-y-4">
        {isOpen && (
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="p-4 border rounded-2xl bg-[#FCFDF8] space-y-4"
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-[#212121]/70 text-base font-medium">
                {isEdit ? t("Edit Price Matrix") : t("Add New Price Matrix")}
              </h3>
            </div>

            {/* FORM FIELDS — SAME AS BEFORE */}
            <div className="grid grid-cols-6 gap-4">
              <div className="col-span-6">
                <div className="flex items-center gap-2 mb-3">
                  <label className="relative flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={forClient}
                      onChange={() => {
                        setForClient(!forClient);
                        if (!forClient) {
                          setClientId("");
                          setWeightRows([]);
                        }
                      }}
                      className="peer h-5 w-5 cursor-pointer appearance-none rounded border border-slate-300 checked:border-[#FBD037] bg-white checked:bg-[#FBD037] checked:text-white"
                    />
                    <span className="absolute text-xs font-bold text-white peer-checked:opacity-100 opacity-0 top-1/2 left-[10px] transform -translate-x-1/2 -translate-y-1/2 pointer-events-none">
                      ✓
                    </span>
                  </label>
                  <span className="text-sm text-[#1E293B] cursor-pointer">
                    {t("For Client")}
                  </span>
                </div>

                {forClient && (
                  <NewSelectInput
                    label={t("Select Client")}
                    selectOptions={
                      weightClients?.map((client: any) => ({
                        id: client?.id,
                        value: client?.id,
                        text: client?.name,
                      })) || []
                    }
                    name="clientForId"
                    clearErrors={clearErrors}
                    register={register}
                    validation={{ required: t("Client is required") }}
                    value={watch("clientForId")}
                    setValue={handleClientChange}
                    error={errors.clientForId}
                    className="col-span-6"
                    disabled={false}
                  />
                )}
              </div>

              <NewSelectInput
                label={t("Warehouse")}
                selectOptions={
                  warehouses?.warehouses?.map((w: any) => ({
                    id: w.id,
                    value: w.id,
                    text: w.name,
                  })) || []
                }
                name="warehouseId"
                clearErrors={clearErrors}
                register={register}
                validation={{ required: t("Warehouse is required") }}
                value={watch("warehouseId")}
                setValue={(val: string) => setValue("warehouseId", val)}
                error={errors.warehouseId}
                className="col-span-2"
              />

              <NewSelectInput
                label={t("Tier")}
                selectOptions={
                  tireData?.map((t: any) => ({
                    id: t?.id,
                    value: t?.id,
                    text: t?.name,
                  })) || []
                }
                name="tireId"
                clearErrors={clearErrors}
                register={register}
                validation={{ required: t("Tier is required") }}
                value={watch("tireId")}
                setValue={(val: string) => setValue("tireId", val)}
                error={errors.tireId}
                className="col-span-2"
              />

              <NewSelectInput
                label={t("Service Type")}
                selectOptions={
                  serviceData?.map((s: any) => ({
                    id: s?.id,
                    value: s?.id,
                    text: s?.name,
                  })) || []
                }
                name="serviceType"
                clearErrors={clearErrors}
                register={register}
                validation={{ required: t("Service Type is required") }}
                value={watch("serviceType")}
                setValue={(val: string) => setValue("serviceType", val)}
                error={errors.serviceType}
                className="col-span-2"
              />

              {/* Weight Ranges */}
              <div className="col-span-6 border-t pt-4 mt-2 space-y-4">
                <p className="text-[#212121] font-medium uppercase text-sm">
                  {t("Price")}
                </p>
                {weightRows?.length > 0 ? (
                  <div className="grid grid-cols-12 gap-4">
                    {weightRows?.map((row, index) => (
                      <div
                        key={index}
                        className="col-span-12 grid grid-cols-12 gap-4"
                      >
                        <NewinputField
                          className="col-span-6"
                          name=""
                          type="text"
                          title={t("Weight Range")}
                          value={row.range}
                          disabled
                        />
                        <NewinputField
                          className="col-span-6"
                          name={`delivery_price_${index}`}
                          type="text"
                          title={t("Rate")}
                          placeholder={t("Enter Rate")}
                          register={register}
                          validation={{ required: t("Rate is required") }}
                          error={errors[`delivery_price_${index}`]}
                          defaultValue={row.delivery_price}
                          other={{
                            onInput: (e: any) => {
                              const value = e.target.value;
                              const numericValue = value.replace(
                                /[^0-9.]/g,
                                ""
                              );
                              const parts = numericValue.split(".");
                              if (parts.length > 2) {
                                e.target.value =
                                  parts[0] + "." + parts.slice(1).join("");
                              } else {
                                e.target.value = numericValue;
                              }
                              if (parts[1] && parts[1].length > 2) {
                                e.target.value =
                                  parts[0] + "." + parts[1].substring(0, 2);
                              }
                            },
                            onKeyPress: (e: any) => {
                              const char = String.fromCharCode(e.which);
                              const currentValue = e.target.value;

                              if (/[0-9]/.test(char)) return;

                              if (char === "." && !currentValue.includes("."))
                                return;
                              e.preventDefault();
                            },
                          }}
                        />
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">
                    {forClient
                      ? clientId
                        ? t("Loading weight ranges...")
                        : t("Select a client")
                      : t("Enable 'For Client'")}
                  </p>
                )}
              </div>

              {/* Return & COD */}
              <div className="col-span-6 border-t pt-4 mt-2 space-y-4">
                <p className="text-[#212121] font-medium uppercase text-sm">
                  {t("Return & COD Fees")}
                </p>
                <div className="grid grid-cols-12 gap-4">
                  <NewinputField
                    className="col-span-6"
                    name="return_price"
                    type="number"
                    title={t("Return Price")}
                    placeholder={t("Enter Return Price")}
                    register={register}
                    value={watch("return_price")}
                    error={errors.return_price}
                  />
                  <NewinputField
                    className="col-span-6"
                    name="cod_fee"
                    type="number"
                    title={t("COD Fee")}
                    placeholder={t("Enter COD Fee")}
                    register={register}
                    value={watch("cod_fee")}
                    error={errors.cod_fee}
                  />
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="submit"
                disabled={
                  createPending ||
                  updatePending ||
                  (forClient && weightRows?.length === 0)
                }
                className="bg-[#FBD037] hover:bg-yellow-400 text-black rounded-full px-6 py-2 text-sm font-medium"
              >
                {isEdit
                  ? updatePending
                    ? t("Updating...")
                    : t("Update")
                  : createPending
                  ? t("Adding...")
                  : t("Add")}
              </button>
              <button
                type="button"
                onClick={handleCancel}
                className="border border-[#D2D2D2] text-[#212121] text-sm font-semibold px-5 py-2 rounded-full"
              >
                {t("Cancel")}
              </button>
            </div>
          </form>
        )}

        {/* List */}
        <div className="-mx-4">
          {isLoading || isFetching ? (
            <TableSkeleton
              mainClass="bg-gradient-to-r from-[#FFFFFD] to-[#FDF8E5]"
              subClassName=""
              columns={[
                "#",
                t("System Type"),
                t("Warehouse"),
                t("Tier"),
                t("Service"),
                t("City"),
                t("Created By"),
                t("Created Type"),
                t("Status"),
                t("Actions"),
              ]}
              rows={6}
            />
          ) : data?.priceMatrix?.length ? (
            <ListView
              mainDivClassName="bg-transparent"
              tdClassName="rounded-r-none rounded-l-none"
              trClassName="hover:bg-[#FBD037]/20"
              mailDivClassName="p-0 bg-transparent"
              thClassName="border-t"
              data={data?.priceMatrix}
              columns={[
                {
                  label: "#",
                  render: (_: any, i: number) => (pageNo - 1) * limit + (i + 1),
                },
                {
                  label: t("System Type"),
                  render: (c: any) => c?.clientName || t("By Default"),
                },
                {
                  label: t("Warehouse"),
                  render: (c: any) => c?.warehouseName || "—",
                },
                { label: t("Tier"), render: (c: any) => c?.tireName || "—" },
                { label: t("Service"), render: (c: any) => c?.serviceName || "—" },
                {
                  label: t("City"),
                  render: (c: any) => (
                    <button
                      onClick={() =>
                        handleViewCities(c.coverageCityNames || [])
                      }
                      className="text-blue-600 underline font-medium"
                    >
                      {t("View")}
                    </button>
                  ),
                },
                {
                  label: t("Created By"),
                  render: (c: any) => c?.createdByName || "—",
                },
                {
                  label: t("Created Type"),
                  render: (c: any) => c?.createdByType || "—",
                },
                {
                  label: t("Status"),
                  render: (c: any) => (
                    <div className="flex justify-center items-center">
                      <ToggleButton
                        onChange={() => {
                          setSelectedPriceMatrix(c);
                          setActiveModal(true);
                        }}
                        checked={c?.status === "ACTIVE"}
                      />
                    </div>
                  ),
                },
                {
                  label: t("Actions"),
                  render: (row) => (
                    <div className="flex justify-center items-center">
                      <button
                        onClick={() => handleEdit(row)}
                        className="p-2 border border-[#D2D2D2] rounded-full hover:bg-gray-50"
                      >
                        <IconPencil size={20} />
                      </button>
                    </div>
                  ),
                },
              ]}
            />
          ) : (
            <NoDataFound
              subClassName=""
              mainClassName="bg-gradient-to-r from-[#FFFFFD] to-[#FDF8E5] rounded-b-3xl"
            />
          )}
          {data?.totalPages > 1 && (
            <div className="flex justify-center items-center pt-2">
              <Pagination
                currentPage={pageNo}
                totalPages={data?.totalPages}
                fetchData={handlePaginationFetch}
              />
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      <MessageModal
        open={activeModal}
        onClose={() => setActiveModal(false)}
        title={t("Confirm")}
        subtitle={t("Change status?")}
        img={
          selectedPriceMatrix?.status === "ACTIVE"
            ? "/img/inactive.svg"
            : "/img/active.svg"
        }
        closeButtonText={t("Cancel")}
        actionButtonText={t("Confirm")}
        actionButtonOnClick={handleUpdateStatus}
        isPending={statusPending}
      />
      <ViewCitiesPriceMatrixModal
        isOpen={cityModalOpen}
        cities={selectedCities}
        onClose={() => setCityModalOpen(false)}
      />
    </div>
  );
}
